import { View, Image } from 'react-native'
import { ATLAS } from './personAvatarAtlas'
import PersonAvatarMedal from './PersonAvatarMedal'
import { PERSON_DEFAULT, HEADWEAR_FALLBACK, TINY_AVATAR_MAX, lighten, darken, lipColor } from './personAvatarConfig'
import { useRemoteHead, headTile } from '../lib/avatarHeads'

/**
 * PersonAvatar — avatar persona a livelli colorabili (uomo/donna).
 *
 * Props:
 * - person: { gender, style, headwear, glasses, jersey, skin, hair, iris, main, accent, accent2, trim, wear, detail, frame, lens }
 * - corridori: uomo style 'h5' (mossi medi) | 'h6' (stempiato) | 'h7' (spettinato) | 'h8' (cortissimo) | 'h9' (riccio corto) | 'h10' (riga di lato) | 'h11' (all'indietro) | 'h12' (in avanti) | 'h13' (attaccatura alta) | 'h14' (ondulati corti) | 'h15' (riccio medio) | 'h16' (stempiato corto), beard 1 ombra | 2 baffi | 3 corta | 4 piena | 6 baffi + pizzetto separati | 7 pizzetto congiunto ai baffi | 8 accenno puntinato (colore = hair; beardA = opacita' 0..1, es. 0.5 per l'ombra molto leggera); donna style 'h5' (lunghi sciolti), 'h6' (coda), 'h7' (chignon), 'h8' (lunghi ondulati), 'h9' (treccia), fringe: true = frangia sopra il taglio
 * - maglie kit squadra (nascoste nell'editor, vedi personFromKit): classica con `accent2` = manica DESTRA di un altro colore (`accent` colora la sinistra);
 *   jersey 7 = degradé verticale (main alto, accent fondo, accent2 maniche, grad 'low'|'mid' = dove parte); jersey 8 = due meta' (main sinistra, accent destra)
 * - uomo: face 'a'..'g' = volto alternativo (magro, robusto, giovane, spigoloso, maturo, sorridente, sorriso chiuso), senza face il viso di serie ('z'); anche con cappellino o casco.
 *   brow 1 sottili | 2 folte. Testa da livelli propri (linee del viso sotto la barba, bocca sopra), barbe adattate al volto
 * - flip: true = capelli specchiati (solo senza copricapo)
 * - head: { slug, v } = testa su misura del corridore o della ciclista, scaricata da Supabase Storage (lib/avatarHeads.js): viso, capelli e barba
 *   disegnati su di lui; colori (pelle, capelli, iride, maglia) come sempre. Finche' non e' scaricata si vede la testa normale (style, face...).
 * - size: lato in px (default 200)
 * - medal: 'gold' | 'silver' | 'bronze' per mettere la medaglia al collo (sopra maglia e contorni, sotto gli occhiali)
 * - special: codice di una maglia speciale di lega ('world_champion'): sostituisce la maglia scelta (vedi lib/useSpecialJerseys.js)
 * - jerseyOnly: solo la maglia con i suoi contorni ripuliti (jersey_line_tile: senza collo e braccia; niente testa, capelli, casco, occhiali): icona maglia della squadra (components/RiderFace.jsx TeamJersey)
 *
 * I livelli stanno in fogli (atlas, components/personAvatarAtlas.js, generati da scripts/build_atlas.py): ogni livello e' una casella
 * di un foglio, mostrata in un riquadro che ritaglia il resto. Le maschere bianche si colorano con tintColor; contorni e ombre restano fissi.
 * Il canvas è quadrato e intero (tronco compreso): lascia spazio a patch/sponsor futuri.
 */
// Sopracciglia: col colore dei capelli, ma con i capelli chiari (biondi, castano chiaro) un po' piu' scure, come nella realta':
// altrimenti sulla pelle chiara quasi non si vedono. Capelli scuri: uguali ai capelli.
function browColor(hair) {
  const n = parseInt(hair.slice(1), 16)
  const lum = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255
  return lum > 0.45 ? darken(hair, 0.62) : hair
}
const SPECIAL_LAYER = { world_champion: 'jersey_world', european_champion: 'jersey_european', worldcup_leader: 'jersey_worldcup' }

export default function PersonAvatar({ person, size = 200, medal = null, special = null, jerseyOnly = false }) {
  const p = { ...PERSON_DEFAULT, ...(person ?? {}) }
  const g = p.gender === 'woman' ? 'woman' : 'man'
  const tier = size <= TINY_AVATAR_MAX ? 'tiny' : 'full'   // due sole versioni dei livelli: 192 px e 512 px
  // livello per nome: dal foglio della dimensione giusta; i livelli solo dei corridori esistono solo a 192 px
  // testa su misura (corridori e cicliste, senza copricapo): livelli 'face_@_*' e '@_hair', '@_hairline' dal foglio scaricato
  const remote = useRemoteHead(!jerseyOnly && p.headwear === 'none' ? p.head : null, tier)
  const find = (name) => {
    if (name.startsWith('face_@_') || name.startsWith('@_')) {
      const t = remote && headTile(name.replace(/^face_@_|^@_/, ''))
      return t ? [remote, t[0], t[1]] : null
    }
    const own = ATLAS[tier][g], t = own.tiles[name]
    if (t) return [own.sheets[t[0]], t[1], t[2]]
    const tiny = ATLAS.tiny[g], u = tiny.tiles[name]
    return u ? [tiny.sheets[u[0]], u[1], u[2]] : null
  }
  const has = (name) => !!find(name)

  const layer = (name, color, key, opacity, flip) => {
    const f = find(name)
    if (!f) return null
    const [sh, c, r] = f
    const cell = sh.s + 2 * sh.g
    // riquadro grande quanto la casella in px del foglio (offset interi), poi scalato tutto insieme: ogni livello ha la stessa
    // geometria e i livelli restano allineati al pixel (con offset frazionari diversi per livello si vedevano doppi contorni)
    return (
      <View key={key} pointerEvents="none" style={{
        position: 'absolute', left: (size - sh.s) / 2, top: (size - sh.s) / 2, width: sh.s, height: sh.s, overflow: 'hidden',
        opacity: opacity ?? 1, transform: flip ? [{ scale: size / sh.s }, { scaleX: -1 }] : [{ scale: size / sh.s }],
      }}>
        <Image source={sh.src} fadeDuration={0} resizeMode="stretch" style={{
          position: 'absolute', left: -(c * cell + sh.g), top: -(r * cell + sh.g),
          width: sh.cols * cell, height: sh.rows * cell, ...(color ? { tintColor: color } : null),
        }} />
      </View>
    )
  }
  const tinted = (name, color, key) => layer(name, color, key)
  const fixed = (name, key) => layer(name, null, key)

  // taglio + copricapo: h5..h16 esistono solo senza copricapo, con cappellino o casco si usa il taglio di serie piu' vicino (HEADWEAR_FALLBACK)
  const styleWithWear = (p.headwear !== 'none' && HEADWEAR_FALLBACK[g]?.[p.style]) || p.style
  const v = [`${p.style}_${p.headwear}`, `${styleWithWear}_${p.headwear}`, 'h1_none'].find(x => has(`${x}_skin`))
  // Maglia speciale (es. campione del mondo): maglia a fasce bianca + livello a colori fissi sopra, al posto dei colori scelti
  const specialName = special && SPECIAL_LAYER[special] && has(SPECIAL_LAYER[special]) ? SPECIAL_LAYER[special] : null
  if (specialName) { p.jersey = 2; p.main = '#FFFFFF'; p.accent = '#FFFFFF'; p.trim = '#FFFFFF' }
  const j = has(`jersey${p.jersey}_main`) || p.jersey === 7 || p.jersey === 8 ? p.jersey : 3

  // Uomo e donna: testa da livelli propri per ogni volto (scripts/build_face_layers.py): a..g volti alternativi, z = viso di serie.
  // La testa viene disegnata dopo la maglia, le linee del viso sotto la barba e la bocca sopra; capelli, casco e cappellino sopra la testa.
  const face = jerseyOnly ? null : remote ? '@' : (p.face && has(`face_${p.face}_skin`) ? p.face : 'z')
  const isBase = face === 'z'
  // testa su misura: la barba (se c'e') e' quella disegnata su di lui, sempre col colore dei capelli
  const beardName = remote ? 'face_@_beard' : p.beard ? (face && !isBase ? `face_${face}_beard${p.beard}` : `beard${p.beard}`) : null
  const hairName = remote ? '@_hair' : `${v}_hair`
  const lineName = remote ? '@_hairline' : p.headwear === 'none' ? `${p.style}_hairline` : `${v}_line`
  // capelli specchiati (coda, treccia, riga dall'altro lato): stessi livelli capovolti, solo senza copricapo; il viso resta com'e'
  const mirror = !!p.flip && !!face && !remote && p.headwear === 'none'
  // donna con testa su misura: corpo senza la testa calva e senza l'ombra del mento della base (scripts/build_woman_head_body.py)
  const hb = remote && g === 'woman' && has('face_body_skin_hb') ? '_hb' : ''

  return (
    <View style={{ width: size, height: size }}>
      {face ? (isBase ? tinted('face_z_body_skin', p.skin, 'body-skin') : [tinted(`face_body_skin${hb}`, p.skin, 'body-skin'), fixed('face_neck_line', 'body-neck')]) : !jerseyOnly && [
        tinted(`${v}_skin`, p.skin, 'skin'),
        tinted('iris', p.iris, 'iris'),
        tinted('brow', browColor(p.hair), 'brow'),
        tinted('lips', lipColor(p.skin), 'lips'),
      ]}
      {j === 7 ? [
        tinted('jersey3_main', p.main, 'j-main'),
        tinted(p.grad === 'low' ? 'jersey_grad_low' : 'jersey_grad_mid', p.accent, 'j-grad'),
        tinted('jersey3_accent', p.accent2 ?? p.main, 'j-sleeves'),
        tinted('jersey3_trim', p.trim, 'j-trim'),
      ] : j === 8 ? [
        tinted('jersey3_main_l', p.main, 'j-main-l'), tinted('jersey3_main_r', p.accent, 'j-main-r'),
        tinted('jersey3_accent_l', p.main, 'j-accent-l'), tinted('jersey3_accent_r', p.accent, 'j-accent-r'),
        tinted('jersey3_trim', p.trim, 'j-trim'),
      ] : [
        tinted(`jersey${j}_main`, p.main, 'j-main'),
        j === 3 && p.accent2
          ? [tinted('jersey3_accent_l', p.accent, 'j-accent-l'), tinted('jersey3_accent_r', p.accent2, 'j-accent-r')]
          : tinted(`jersey${j}_accent`, p.accent, 'j-accent'),
        tinted(`jersey${j}_trim`, p.trim, 'j-trim'),
      ]}
      {specialName && fixed(specialName, 'j-special')}
      {fixed(jerseyOnly ? 'jersey_line_tile' : 'jersey_line', 'j-line')}
      {face && [
        tinted(`face_${face}_skin`, p.skin, 'face-skin'),
        tinted(`face_${face}_iris`, p.iris, 'face-iris'),
        tinted(`face_${face}_lips`, lipColor(p.skin), 'face-lips'),
        tinted(p.brow && has(`face_${face}_brow${p.brow}`) ? `face_${face}_brow${p.brow}` : `face_${face}_brow`, browColor(p.hair), 'face-brow'),
        fixed(`face_${face}_ink`, 'face-ink'),
      ]}
      {!jerseyOnly && g === 'man' && beardName && layer(beardName, p.hair, 'beard', remote ? null : p.beardA)}
      {remote && fixed('face_@_beardline', 'beard-line')}
      {face && [fixed(`face_${face}_mouth`, 'face-mouth'), fixed(`face_${face}_white`, 'face-white')]}
      {face && fixed(isBase ? 'face_z_body_detail' : `face_body_detail${hb}`, 'body-detail')}
      {!jerseyOnly && layer(hairName, p.hair, 'hair', null, mirror)}
      {!jerseyOnly && tinted(`${v}_wear`, p.wear, 'wear')}
      {!jerseyOnly && tinted(`${v}_visor`, p.detail, 'visor')}
      {!jerseyOnly && tinted(`${v}_vents`, p.detail, 'vents')}
      {face ? layer(lineName, null, 'hairline', null, mirror) : !jerseyOnly && fixed(`${v}_detail`, 'detail')}
      {!jerseyOnly && g === 'woman' && p.fringe && !remote && p.headwear === 'none' && [layer('fringe1', p.hair, 'fringe', null, mirror), layer('fringe1_detail', null, 'fringe-d', null, mirror)]}
      {medal && <PersonAvatarMedal key="medal" kind={medal} size={size} />}
      {!jerseyOnly && p.glasses && [
        tinted('glasses_frame', p.frame, 'g-frame'), tinted('glasses_lens1', p.lens, 'g-lens1'),
        tinted('glasses_lens2', lighten(p.lens), 'g-lens2'), fixed('glasses_detail', 'g-detail'),
      ]}
    </View>
  )
}
